import React, {createContext, useContext, useEffect, useMemo, useState} from "react";

export type ColorSchemePreference = "light" | "dark" | "system";
export type ResolvedColorScheme = "light" | "dark";

interface ColorSchemeContextValue {
    preference: ColorSchemePreference;
    resolved: ResolvedColorScheme;
    setPreference: (next: ColorSchemePreference) => void;
}

const STORAGE_KEY = "an-color-scheme";
const ColorSchemeContext = createContext<ColorSchemeContextValue | null>(null);

const readStored = (): ColorSchemePreference => {
    if (typeof window === "undefined") return "system";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : "system";
};

const systemScheme = (): ResolvedColorScheme =>
    typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";

const resolveScheme = (pref: ColorSchemePreference): ResolvedColorScheme =>
    pref === "system" ? systemScheme() : pref;

interface ColorSchemeProviderProps {
    children: React.ReactNode;
}

export const ColorSchemeProvider = ({children}: ColorSchemeProviderProps) => {
    const [preference, setPreferenceState] = useState<ColorSchemePreference>(readStored);
    const [resolved, setResolved] = useState<ResolvedColorScheme>(() => resolveScheme(readStored()));

    // Apply the data-theme attribute + persistence whenever the preference changes.
    useEffect(() => {
        const next = resolveScheme(preference);
        setResolved(next);
        document.documentElement.dataset.theme = next;
        if (preference === "system") {
            window.localStorage.removeItem(STORAGE_KEY);
        } else {
            window.localStorage.setItem(STORAGE_KEY, preference);
        }
    }, [preference]);

    // While preference === "system", track OS-level changes live.
    useEffect(() => {
        if (preference !== "system") return;
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const onChange = () => {
            const next: ResolvedColorScheme = mq.matches ? "dark" : "light";
            setResolved(next);
            document.documentElement.dataset.theme = next;
        };
        mq.addEventListener("change", onChange);
        return () => { mq.removeEventListener("change", onChange); };
    }, [preference]);

    const value = useMemo(
        () => ({preference, resolved, setPreference: setPreferenceState}),
        [preference, resolved],
    );

    return <ColorSchemeContext.Provider value={value}>{children}</ColorSchemeContext.Provider>;
};

export const useColorScheme = () => {
    const ctx = useContext(ColorSchemeContext);
    if (!ctx) throw new Error("useColorScheme must be used inside ColorSchemeProvider");
    return ctx;
};
